"use client";

import React from "react";
import { PageEventThankYouSchema } from "@/sanity/pages/PageEventThankYouTemplate";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import ChipSerif from "@/components/global/ui/ChipSerif";
import Footer from "@/components/global/partials/Footer";
import Image from "@/helpers/Image";
import bg from "@/public/event/bg-hero.jpg";
import lastBG from "@/public/event/last-bg.jpg";
import atlas from "@/public/event/atlas.webp";
import Countdown from "../global/countdown/Countdown";
import logoWhite from "@/public/logo-white.svg";
import ReviewsSection from "../pageComponents/challenge/Reviews";
import { easeOut, motion } from "framer-motion";

export default function PageEventThankYouJSX({
  data,
}: {
  data: PageEventThankYouSchema;
}) {
  const [mousePosition, setMousePosition] = React.useState<{
    [key: number]: { x: number; y: number };
  }>({});
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  const handleMouseMove = (
    e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>,
    index: number,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePosition((prev) => ({ ...prev, [index]: { x, y } }));
  };

  return (
    <>
      <main className="bg-[#07171B]">
        {/* Hero Section */}
        <section className="p-[1rem] lg:px-[3rem] lg:pt-[3rem]">
          <div className="flex relative flex-col justify-between items-center w-full bg-transparent lg:pb-0 overflow-clip bg-blend-darken rounded-[1.75rem] h-[97.375rem] px-[1.25rem] pb-[4rem] lg:justify-normal lg:gap-[6rem] lg:h-[107.5rem] lg:rounded-[4rem] lg:px-[3rem]">
            <div className="flex absolute justify-between items-center w-[min(146.5rem,100%)] lg:top-[3rem] z-[5] px-[2rem] lg:px-0 top-[2rem]">
              <Image src={logoWhite} alt="" className="" />

              <div className="flex items-center rounded-full bg-[#FF5F5F]/10 w-fit px-[1.5rem] py-[1rem] gap-[1rem]">
                <div className="rounded-full w-[0.875rem] h-[0.875rem] bg-[#FFBABA]" />
                <p className="m-0 text-[1.75rem] text-[#FFBABA]">
                  {data.heroHeaderDate}
                </p>
              </div>
            </div>
            <div className="absolute bottom-0 w-full bg-gradient-to-b from-transparent opacity-20 h-[69.75rem] to-[#A3DEB2] z-[2]" />

            <Image
              src={bg}
              alt=""
              quality={100}
              className="object-cover absolute w-full !h-full"
            />

            <div className="flex relative flex-col items-center z-[9] pt-[12rem] gap-[1.5rem] lg:pt-[8rem]">
              {data?.heroChip && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="mx-auto rounded-full border px-[1.5rem] w-fit border-white/[16%] mb-[1rem]"
                >
                  <ChipSerif
                    text={data.heroChip}
                    className="m-0 uppercase text-white/60 font-geist text-[1.75rem] font-[300] tracking-[.07rem]"
                  />
                </motion.div>
              )}

              {data?.heroText && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.3, delay: 0.075, ease: easeOut }}
                  className="richtext w-[min(85rem,100%)] mb-[.5rem]
                  prose-h1:text-white prose-p:text-white/70 lg:prose-h1:!text-[8rem] prose-h1:!text-[5rem] prose-h1:mb-[1.5rem] prose-h1:leading-[86%] prose-h1:font-[500]
                  prose-p:text-[2rem] prose-p:mb-0
                  prose-strong:font-[400] prose-strong:text-white prose-strong:pr-[.5rem] lg:prose-strong:pr-0
                  !prose-span:font-[300]"
                >
                  <PortableText
                    value={data.heroText}
                    components={richTextComponent}
                  />
                </motion.div>
              )}

              {data?.heroCta && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.3,
                    delay: 0.15,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                  className="mx-auto w-full lg:w-fit"
                >
                  <CTAButton data={data.heroCta} />
                </motion.div>
              )}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, delay: 0.225, ease: easeOut }}
              className="flex flex-col items-center gap-[.5rem] w-[min(72rem,100%)] relative z-[9]"
            >
              <div className="grid grid-cols-2 w-full gap-[.5rem] pt-[1rem]">
                {data?.heroInfos &&
                  data?.heroInfos.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
                      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{
                        duration: 0.3,
                        delay: 0.3 + index * 0.075,
                        ease: [0.25, 0.4, 0.25, 1],
                      }}
                      className="flex relative items-center border p-[1rem] bg-[#07171B]/50 backdrop-blur-[2rem] rounded-[1.5rem] border-white/20 gap-[1rem] lg:p-[2rem] lg:rounded-[2rem] lg:gap-[2rem]"
                    >
                      <div className="w-[7.25rem] h-[7.25rem] bg-[#A3DEB2]/[15%] rounded-[1.375rem] flex items-center justify-center">
                        <Image src={item.icon} alt="" />
                      </div>
                      <div className="richtext richtext-start prose-p:text-[1.75rem] prose-p:font-[400] prose-p:m-0 prose-strong:text-[2rem] prose-strong:text-white prose-strong:font-[500] prose-p:text-white/70 lg:prose-p:text-[2rem] lg:prose-strong:text-[2.25rem]">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    </motion.div>
                  ))}
              </div>

              {data?.heroCountdownToggle && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.3,
                    delay: 0.6,
                    ease: easeOut,
                  }}
                  className="w-full border bg-[#07171B]/50 backdrop-blur-[2rem] rounded-[1.5rem] border-white/20 lg:rounded-[2rem]"
                >
                  <div className="prose-h2:text-white prose-h6:text-[#A3DEB2] [&_*]:border-white/20">
                    <Countdown
                      dateTimeString={data?.heroCountdown}
                      lang={data?.heroCountdownTextLang ?? "de"}
                    />
                  </div>
                  <div className="w-full border-t py-[2rem] border-white/20">
                    <p className="m-0 text-center uppercase text-[2rem] text-white/80">
                      {data.heroCountdownTextBottom}
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>

        <section className="w-[min(176.5rem,100%)] flex flex-col items-center gap-[12rem] lg:gap-[25rem] mx-auto px-[2rem] py-[8rem] lg:py-[12rem]">
          {/* Cards*/}
          <div className="flex relative flex-col items-center gap-[1rem] z-[9] lg:gap-[2rem]">
            {data?.cardsTitle && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.3,
                  delay: 0,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="richtext w-[min(78.875rem,100%)] mb-[2rem] prose-h2:!text-white prose-h2:max-w-[80vw] prose-p:text-white/70 prose-p:w-[min(56.875rem,100%)] prose-p:font-[300] prose-p:max-w-[90vw] mx-auto lg:[&_br]:hidden [&_span]:text-[#A3DEB2]"
              >
                <PortableText
                  value={data.cardsTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-[1rem] lg:gap-[2rem]">
              {data?.cardsItems.map((item, index) => {
                const bgColors = ["#85D0A6", "#93BCDB", "#FFF7E2"];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.3,
                      delay: 0.075 + index * 0.075,
                      ease: [0.25, 0.4, 0.25, 1],
                    }}
                    className="flex relative flex-col p-[1rem] rounded-[2rem] lg:rounded-[2.25rem]"
                    style={{ backgroundColor: bgColors[index] ?? bgColors[0] }}
                  >
                    <div className="relative w-full h-[31rem] overflow-clip rounded-[2rem]">
                      <Image
                        src={item.bg}
                        alt=""
                        className="object-cover !w-full !h-full"
                        quality={100}
                      />
                    </div>
                    <div className="flex flex-col flex-1 justify-between p-[1.5rem] pt-[3rem] gap-[2rem]">
                      <div className="richtext richtext-start prose-h4:pt-0 prose-h4:m-0 prose-p:m-0 prose-h4:mb-[1.12rem]">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            {data?.cardsLowerText && (
              <motion.p
                initial={{
                  opacity: 0,
                  scale: 0.9,
                  filter: "blur(10px)",
                  y: -30,
                }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                  filter: "blur(0px)",
                  y: 0,
                }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.3,
                  delay: 0.3,
                  ease: easeOut,
                }}
                className="m-0 text-[2.5rem] text-white font-geist font-[400] leading-[135%] py-[2.5rem] w-full text-center rounded-[2rem] lg:rounded-[1rem] bg-[#103037]"
              >
                {data.cardsLowerText}
              </motion.p>
            )}
          </div>

          {/* Social Media */}
          <div className="flex relative flex-col justify-center items-center gap-[3rem] z-[9] lg:gap-[4rem]">
            {data?.socialMediaTitle && (
              <motion.div
                initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.3, delay: 0, ease: easeOut }}
                className="richtext w-[min(78.875rem,100%)] prose-h6:text-[2.25rem] prose-h6:text-white prose-h6:font-serif prose-h6:font-[400] prose-h2:!text-white prose-p:text-white/70 prose-p:w-[min(56.875rem,100%)] prose-p:font-[300] mx-auto [&_span]:text-[#A3DEB2]"
              >
                <PortableText
                  value={data.socialMediaTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            <div className="grid lg:grid-cols-3 grid-cols-1 gap-[1rem] lg:gap-[2rem] w-[min(104.5rem,100%)]">
              {data?.socialMediaItems?.map((item, index) => {
                const bgColors = [
                  "radial-gradient(116.99% 95.96% at 52.76% 0%, rgba(163, 222, 178, 0.32) 0%, rgba(163, 222, 178, 0.00) 100%), rgba(163, 222, 178, 0.15)",
                  "radial-gradient(116.99% 95.96% at 52.76% 0%, rgba(162, 225, 255, 0.32) 0%, rgba(162, 225, 255, 0.00) 100%), rgba(162, 225, 255, 0.15)",
                ];
                const widthLogo = ["5.75rem", "5.382rem", "6.25rem"];
                const pos = mousePosition[index];
                const isHovered = hoveredIndex === index;

                const content = (
                  <>
                    {/* Mouse Follow Light Effect */}
                    {pos && (
                      <div
                        className="absolute transition-opacity duration-500 ease-out pointer-events-none z-[3]"
                        style={{
                          left: `${pos.x}px`,
                          top: `${pos.y}px`,
                          width: "200px",
                          height: "200px",
                          transform: "translate(-50%, -50%)",
                          background:
                            "radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 70%)",
                          filter: "blur(20px)",
                          opacity: isHovered ? 1 : 0,
                        }}
                      />
                    )}

                    {/* Camada da Borda Gradiente Bottom-Right */}
                    <div
                      className="absolute inset-0 bg-gradient-to-br pointer-events-none rounded-[8.79363rem] from-white/50 via-white/5 to-white/50 z-[2]"
                      style={{
                        paddingTop: "0.15rem",
                        paddingRight: "0.15rem",
                        WebkitMask:
                          "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                        WebkitMaskComposite: "xor",
                        maskComposite: "exclude",
                      }}
                    />

                    <div
                      className="flex relative items-center scale-75 lg:scale-100 z-[5]"
                      style={{
                        width: widthLogo[index] ?? widthLogo[0],
                      }}
                    >
                      <Image
                        src={item.icon}
                        alt=""
                        className="object-cover !w-full"
                        quality={100}
                      />
                    </div>
                    <span className="relative m-0 leading-none text-white text-[2.5rem] font-geist font-[400] z-[5]">
                      {item.text}
                    </span>
                  </>
                );

                const WrapperComponent = item.link ? motion.a : motion.div;
                const wrapperProps = item.link
                  ? {
                      href: item.link,
                      target: "_blank",
                      rel: "noopener noreferrer",
                    }
                  : {};

                return (
                  <WrapperComponent
                    key={index}
                    {...wrapperProps}
                    initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.3,
                      delay: 0.075 + index * 0.075,
                      ease: easeOut,
                    }}
                    className="flex relative flex-row justify-center items-center transition-transform duration-300 cursor-pointer gap-[1rem] h-[7rem] p-[2rem] rounded-[12.5rem] overflow-clip lg:gap-[2rem] lg:h-[12.25rem] lg:py-[4rem] lg:px-[6rem] hover:scale-[1.02]"
                    style={{
                      background: bgColors[index] ?? bgColors[0],
                    }}
                    onMouseMove={(e) => handleMouseMove(e, index)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => {
                      setHoveredIndex(null);
                      // Delay clearing position to allow fade out animation
                      setTimeout(() => {
                        setMousePosition((prev) => {
                          const newPos = { ...prev };
                          delete newPos[index];
                          return newPos;
                        });
                      }, 500);
                    }}
                  >
                    {content}
                  </WrapperComponent>
                );
              })}
            </div>

            {data?.socialMediaCTA && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.3, delay: 0.3, ease: easeOut }}
                className="mx-auto w-full lg:w-fit"
              >
                <CTAButton data={data.socialMediaCTA} />
              </motion.div>
            )}
          </div>

          {/* Reviews */}
          <ReviewsSection
            reviewsDocument={data.reviewsDocument}
            className="w-full prose-headings:!text-white prose-headings:!font-[500] prose-p:!text-white/70 prose-p:!font-[300] !px-0 !py-0"
            classNameReviewsSection="!pt-[4rem] !pb-0"
            fadeColor="blue"
            slug={data.settings.slug?.current}
            reviewsChip={data.reviewsChip}
            reviewsTitle={data.reviewsTitle}
            reviewsCTA={data.reviewsCTA}
          />
        </section>

        {/* Last Section */}
        <section className="relative p-[3rem] h-[90rem] lg:h-[130rem]">
          <div className="relative w-full h-full lg:p-0 overflow-clip rounded-[1.5rem] p-[2rem] lg:rounded-[4rem]">
            <div className="relative z-[9] lg:pt-[12.5rem]">
              <div className="richtext richtext-center prose-h6:text-[2.25rem] prose-h6:text-white prose-h6:font-instrument prose-h6:font-[400] prose-h6:mb-[1.5rem] prose-h2:text-white prose-p:font-[300] prose-p:!text-white/70 prose-p:w-[min(54.75rem,100%)] prose-strong:font-[300] prose-strong:text-white mx-auto w-fit">
                <PortableText
                  value={data.lastText}
                  components={richTextComponent}
                />
              </div>

              <div className="bg-[#07171B]/50 backdrop-blur-[2rem] w-[min(69rem,100%)] lg:rounded-[2rem] rounded-[1.5rem] border border-white/20 mx-auto mt-[2rem]">
                <div className="prose-h2:text-white prose-h6:text-[#A3DEB2] [&_*]:border-white/20">
                  <Countdown
                    dateTimeString={data?.lastCountdown}
                    lang={data?.lastCountdownTextLang ?? "de"}
                  />
                </div>
                <div className="w-full border-t py-[2rem] border-white/20">
                  <p className="m-0 text-center text-white">
                    {data.heroCountdownTextBottom}
                  </p>
                </div>
              </div>
            </div>
            <Image
              src={atlas}
              alt=""
              className="absolute bottom-0 left-1/2 lg:-translate-x-1/2 z-[2] !w-[116rem] scale-150 lg:scale-100 -translate-y-[25%] lg:translate-y-0 -translate-x-[40%]"
            />
            <Image
              src={lastBG}
              alt=""
              className="object-cover absolute inset-0 w-full !h-full z-[1]"
              quality={100}
            />
          </div>
        </section>

        {data?.settings?.footer && (
          <Footer
            className="[&_*]:text-white [&_.riskdisclosure_*]:!text-white/50"
            data={data.settings.footer}
            slug={data?.settings?.slug?.current}
          />
        )}
      </main>
    </>
  );
}
